import type { ChangeEvent, FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { ImageUp, LoaderCircle, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LOGO_MAX_SIZE } from '@/constants/clinic'
import { ToothIcon } from '@/components/generals/brand-mark'

interface LogoFieldProps {
    value: string | null
    onChange: (value: string | null) => void
}

const MAX_FILE_BYTES = 5 * 1024 * 1024

const resizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onerror = () => reject(new Error('The file could not be read.'))
        reader.onload = () => {
            const image = new Image()

            image.onerror = () => reject(new Error('This image format is not supported.'))
            image.onload = () => {
                const ratio = Math.min(1, LOGO_MAX_SIZE / Math.max(image.width, image.height))
                const width = Math.max(1, Math.round(image.width * ratio))
                const height = Math.max(1, Math.round(image.height * ratio))
                const canvas = document.createElement('canvas')

                canvas.width = width
                canvas.height = height

                const context = canvas.getContext('2d')
                if (!context) {
                    reject(new Error('The image could not be processed.'))
                    return
                }

                context.drawImage(image, 0, 0, width, height)
                resolve(canvas.toDataURL('image/png'))
            }

            image.src = String(reader.result)
        }

        reader.readAsDataURL(file)
    })
}

const LogoField: FC<LogoFieldProps> = ({ value, onChange }): ReactNode => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return

        if (!file.type.startsWith('image/')) {
            setError('Choose an image file such as PNG or JPG.')
            return
        }

        if (file.size > MAX_FILE_BYTES) {
            setError('The image is larger than 5 MB. Choose a smaller file.')
            return
        }

        setBusy(true)
        setError(null)

        try {
            onChange(await resizeImage(file))
        }
        catch (caught) {
            setError(caught instanceof Error ? caught.message : 'The image could not be processed.')
        }
        finally {
            setBusy(false)
        }
    }

    return (
        <div className='flex flex-col gap-2'>
            <div className='flex flex-wrap items-center gap-4'>
                <div className='flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-border bg-muted/50'>
                    {value ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={value} alt='Clinic logo' className='size-full object-contain p-1.5' />
                    ) : (
                        <ToothIcon className='size-8 text-muted-foreground' />
                    )}
                </div>

                <div className='flex flex-col gap-2'>
                    <div className='flex flex-wrap gap-2'>
                        <Button type='button' variant='outline' onClick={() => inputRef.current?.click()} disabled={busy}>
                            {busy ? <LoaderCircle className='animate-spin' /> : <ImageUp />}
                            {value ? 'Replace logo' : 'Upload logo'}
                        </Button>

                        {value && (
                            <Button type='button' variant='ghost' onClick={() => onChange(null)} className='text-muted-foreground hover:text-destructive'>
                                <Trash2 />
                                Remove
                            </Button>
                        )}
                    </div>

                    <p className='text-xs text-muted-foreground'>
                        PNG or JPG. It is resized automatically and shown in the sidebar and on prescriptions.
                    </p>
                </div>
            </div>

            {error && (
                <p role='alert' className='text-xs font-medium text-destructive'>
                    {error}
                </p>
            )}

            <input
                ref={inputRef}
                type='file'
                accept='image/png,image/jpeg,image/webp'
                className='sr-only'
                tabIndex={-1}
                aria-hidden='true'
                onChange={handleFile}
            />
        </div>
    )
}

export default LogoField
